import { AtlasButtonProps, Icon, Link } from "@resource/atlas";
import { atlasCalendarCheck, atlasRingInfo } from "@resource/atlas/icons";
import clsx from "clsx";
import { MouseEventHandler, useMemo } from "react";
import { CalendarEvent } from "shared/guide-content/rich-blocks/calendar-utils/types";
import { getFormattedEventTimeRangeWithDay } from "shared/guide-content/rich-blocks/calendar-utils/utils";

// component
// ---------

export type ShareAvailabilityButtonProps = AtlasButtonProps & {
  availabilitySubmitted?: boolean;
};

type CandidateAvailabilityProps = {
  availabilities?: CalendarEvent[];
  isMostRecent?: boolean;
};
function CandidateAvailability({
  availabilities = [],
  isMostRecent,
}: CandidateAvailabilityProps) {
  const availabilitiesSanitized = useMemo(
    () =>
      availabilities
        .map((availability) => ({
          ...availability,
          start: availability.start ? new Date(availability.start) : new Date(),
          end: availability.end ? new Date(availability.end) : new Date(),
        }))
        .sort((a, b) => a.start.getTime() - b.start.getTime()),
    [availabilities]
  );

  if (!availabilities.length) {
    return null;
  }

  return (
    <section className="space-y-3">
      {availabilitiesSanitized.map((availability) => (
        <div className="flex items-center gap-2">
          <Icon
            content={atlasCalendarCheck}
            className="w-[1.25rem] h-[1.25rem] text-subtle"
          />
          <span
            className={clsx({
              "line-through text-subtle": !isMostRecent,
            })}
          >
            {getFormattedEventTimeRangeWithDay(availability)}
          </span>
        </div>
      ))}
    </section>
  );
}

type ShareAvailabilityDisplayProps = {
  title: string;
  candidateName?: string;
  className?: string;
  onClickChange?: MouseEventHandler<HTMLButtonElement>;
  availabilities?: CalendarEvent[];
  isMostRecent?: boolean;
  isRevision?: boolean;
};

export function ShareAvailabilityDisplay({
  title,
  candidateName,
  className,
  onClickChange,
  availabilities,
  isMostRecent,
  isRevision,
}: ShareAvailabilityDisplayProps) {
  const availabilitySubmitted = !!availabilities?.length;
  const description = useMemo(() => {
    if (isRevision) {
      return "Updated availability for ";
    }
    if (candidateName) {
      return `${candidateName}'s availability for `;
    }
    return "Availability for ";
  }, [candidateName, isRevision]);
  return (
    <div className={clsx("p-4 shadow-1 rounded-md mt-2 space-y-4", className)}>
      <p>
        {description}
        <span className="text-body-sm-heavy">{title}</span>
      </p>
      {availabilitySubmitted && (
        <CandidateAvailability
          availabilities={availabilities}
          isMostRecent={isMostRecent}
        />
      )}
      {!!onClickChange && isMostRecent && (
        <div className="text-subtle text-body-md">
          Need to make a change?{" "}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link as="button" onClick={onClickChange}>
            Change availability
          </Link>
        </div>
      )}
      {!isMostRecent && (
        <div className="flex items-center gap-2 text-blue-500">
          <Icon content={atlasRingInfo} className="w-[1.25rem] h-[1.25rem] " />
          <span>Availability updated</span>
        </div>
      )}
    </div>
  );
}
